import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  PrimaryButtonMediumV2,
  TertiaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import { CheckBox } from "../../../../../components/CheckBoxes/CheckBoxes";
import { Modal } from "../../../../../components/Modal/Modal";
import {
  Text2xlSemibold,
  TextXlSemibold,
} from "../../../../../components/Typography/TypographyV2";
import type { CompareSection } from "./CompareProducts.util";
import { exportCompareZip } from "./compareExport";
import type { PIMProduct } from "../../../../../types/types.PIM";

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  max-height: 360px;
  overflow: auto;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  cursor: pointer;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
`;

type ExportCompareModalProps = {
  show: boolean;
  onClose: () => void;
  products: PIMProduct[];
  sections: CompareSection[];
  /** Singular entity name for the zip / modal title (product, customer, …). */
  entityName?: string;
};

function titleCaseEntity(name: string): string {
  if (!name) return "Product";
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function ExportCompareModal({
  show,
  onClose,
  products,
  sections,
  entityName = "product",
}: ExportCompareModalProps) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const entityTitle = titleCaseEntity(entityName);

  useEffect(() => {
    if (show) {
      setSelectedIds(new Set(sections.map((s) => s.id)));
    }
  }, [show, sections]);

  const selectedSections = useMemo(
    () => sections.filter((s) => selectedIds.has(s.id)),
    [sections, selectedIds]
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExport = async () => {
    if (!selectedSections.length) return;
    setExporting(true);
    try {
      await exportCompareZip(products, selectedSections, { entityName });
      onClose();
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal show={show} closeModal={onClose} overlay modalWidth="480px">
      <ModalBody>
        <Text2xlSemibold>
          {t("Export {{entity}} Comparison", { entity: entityTitle })}
        </Text2xlSemibold>
        <TextXlSemibold>
          {t("Select Groups/Collections to export.")}
        </TextXlSemibold>
        <OptionsList>
          {sections.map((section) => (
            <OptionRow key={section.id}>
              <CheckBox
                type="checkbox"
                checked={selectedIds.has(section.id)}
                onChange={() => toggle(section.id)}
              />
              <span>{section.display_name}</span>
            </OptionRow>
          ))}
        </OptionsList>
        <Footer>
          <TertiaryButtonMediumV2 onClick={onClose}>
            {t("Cancel")}
          </TertiaryButtonMediumV2>
          <PrimaryButtonMediumV2
            onClick={handleExport}
            disabled={!selectedSections.length || exporting}
            loading={exporting}
          >
            {t("Export")}
          </PrimaryButtonMediumV2>
        </Footer>
      </ModalBody>
    </Modal>
  );
}
